/**
 * 未送信の回答（オフラインキュー）の同期トリガ。design §4.6
 * 起動時／window の online イベント／Capacitor appStateChange(isActive) で
 * syncPendingAnswers を実行する。
 */
import { useEffect, useRef } from "react";
import { App } from "@capacitor/app";
import { syncPendingAnswers } from "./answerQueue";
import { subscribeNetworkStatus } from "../native/networkStatus";

export function useAnswerSync(idToken?: string): void {
  // idToken は 45分ごとのリフレッシュ等で変わるが、そのたびにリスナーを
  // 再登録したくないため ref で最新値だけ追従させる
  const idTokenRef = useRef(idToken);
  idTokenRef.current = idToken;

  useEffect(() => {
    const sync = () => {
      syncPendingAnswers(idTokenRef.current).catch(() => {});
    };

    window.addEventListener("online", sync);

    // OS が接続回復を通知したとき（navigator.onLine の online イベントは
    // 取りこぼすことがあるため、こちらを主トリガとして併用する）
    const unsubscribeNetwork = subscribeNetworkStatus((connected) => {
      if (connected) sync();
    });

    let removeAppStateListener: (() => void) | undefined;
    let unsubscribed = false;
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) sync();
    }).then((handle) => {
      if (unsubscribed) {
        handle.remove();
      } else {
        removeAppStateListener = () => handle.remove();
      }
    });

    return () => {
      unsubscribed = true;
      window.removeEventListener("online", sync);
      unsubscribeNetwork();
      removeAppStateListener?.();
    };
  }, []);

  // 起動直後の同期。セッション復元は非同期なので、マウント時点ではまだ idToken が
  // 無いことがある。その状態で送ると 401 で失敗し、キューは次のトリガ（接続変化・
  // 復帰）まで残りっぱなしになる（2026-09-20 に未送信3件が消えない事象として発生）。
  // トークンが入った時点と、以後のリフレッシュのたびに実行する。
  useEffect(() => {
    if (!idToken) return;
    syncPendingAnswers(idToken).catch(() => {});
  }, [idToken]);
}
